import { IconApplication } from 'assets/icons';
import { AxiosError, AxiosResponse } from 'axios';
import { FilterBlock } from 'components/FiltersBlock';
import { useAppSelector } from 'hooks/useAppSelector';
import { NoneOrders } from 'pages/Orders/NoneOrders';
import { determinateMyExecutorsFiltersCount } from 'pages/Orders/utils';
import { useEffect } from 'react';
import { useMutation } from 'react-query';
import { useDispatch } from 'react-redux';
import { MultiValue, SingleValue } from 'react-select';
import { modalActions } from 'reduxStore/reducers/ModalSlice';
import { ordersActions } from 'reduxStore/reducers/OrdersSlice';
import { getExecutorsPageFilters } from 'reduxStore/reducers/OrdersSlice/selectors/getExecutorsPageFilters';
import { EdmsService } from 'services/EdmsService';
import { Contracts } from 'services/EdmsService/types';
import { BaseError } from 'types';
import { OptionType } from 'ui/redesign/Select/types';
import { Table } from 'ui/redesign/Table';
import { TableCard } from 'ui/redesign/Table/TableCard';
import { TableLoading } from 'ui/redesign/Table/TableLoading';
import { alertBaseError, prepareFilterData } from 'utils/helpers';
import { myExecutorsTableHeadings } from './consts';
import { ExecutorOrganizationsListForm } from './ExecutorOrganizationsListForm';
import { MyExecutorsFiltersModal } from './MyExecutorsFiltersForm';
import { MyExecutorsFiltersFormValues } from './MyExecutorsFiltersForm/types';

export const MyExecutors = () => {
  const dispatch = useDispatch();
  const filters = useAppSelector(getExecutorsPageFilters);

  const {
    mutateAsync: getExecutorOrganizations,
    isLoading,
    data,
  } = useMutation<
    AxiosResponse<Contracts>,
    AxiosError<BaseError>,
    {
      nameTemplate: string;
      tinTemplate: string;
      ftsStatuses: number[];
    }
  >({
    mutationKey: 'getExecutorOrganizations',
    mutationFn: ({ nameTemplate, tinTemplate, ftsStatuses }) => {
      return EdmsService.getExecutorOrganizations({
        nameTemplate,
        tinTemplate,
        ftsStatuses,
        skipCount: 0,
        maxResultCount: 999,
      });
    },
    onError: (err: AxiosError<BaseError>) => {
      alertBaseError(err);
    },
  });

  const handleRowClick = (tableRow: TableData): void => {
    dispatch(
      modalActions.handleOpenModal({
        content: <ExecutorOrganizationsListForm tableRow={tableRow} />,
        options: {
          withCloseButton: true,
          title: 'Данные о самозанятом',
          modalClassName:
            'w-full sm:min-w-[500px] md:min-w-[600px] lg:min-w-[900px] xl:min-w-[1000px] 2xl:min-w-[1400px] !overflow-visible overflow-x-scroll',
          contentClassName: '!overflow-visible',
        },
      })
    );
  };

  useEffect(() => {
    const ftsStatusValues = prepareFilterData(
      filters.ftsStatusName as unknown as MultiValue<OptionType> | SingleValue<OptionType>
    );

    getExecutorOrganizations({
      nameTemplate: filters.fullName,
      tinTemplate: filters.tin,
      ftsStatuses: ftsStatusValues,
    });
  }, []);

  const handleOnFilter = (newFilters: MyExecutorsFiltersFormValues) => {
    const ftsStatusValues = prepareFilterData(
      newFilters.ftsStatusName as unknown as MultiValue<OptionType> | SingleValue<OptionType>
    );

    const filtersBody = {
      nameTemplate: newFilters.fullName,
      tinTemplate: newFilters.tin,
      ftsStatuses: ftsStatusValues,
    };

    dispatch(ordersActions.setExecutorsPageFilters(newFilters));
    getExecutorOrganizations(filtersBody);
  };

  const handleOpenFilterModal = (): void => {
    dispatch(
      modalActions.handleOpenModal({
        content: <MyExecutorsFiltersModal onFilter={handleOnFilter} />,
        options: {
          withCloseButton: false,
          modalClassName: 'w-full sm:w-[650px] !overflow-visible',
          contentClassName: '!overflow-visible',
        },
      })
    );
  };

  return (
    <section className="mt-6 flex flex-col gap-6">
      <FilterBlock
        countFilters={determinateMyExecutorsFiltersCount(filters)}
        onFilterButtonClick={handleOpenFilterModal}
      />
      {isLoading ? (
        <TableLoading />
      ) : (
        <>
          {data?.data.totalCount === 0 ? (
            <div className="min-h-[90vh] overflow-hidden rounded-[10px] bg-base p-8 shadow-3xl">
              <NoneOrders
                icon={<IconApplication />}
                heading={
                  determinateMyExecutorsFiltersCount(filters)
                    ? 'Исполнители не найдены'
                    : 'Исполнителей нет'
                }
                description={
                  determinateMyExecutorsFiltersCount(filters)
                    ? 'Измените настройки фильтра и попробуйте еще раз'
                    : 'Как только Исполнители появятся, они будут отображаться в данном разделе.'
                }
              />
            </div>
          ) : (
            <TableCard>
              <Table
                tableHeading={myExecutorsTableHeadings}
                tableData={(data?.data.items || []) as unknown as TableData[]}
                onRowClick={handleRowClick}
              />
            </TableCard>
          )}
        </>
      )}
    </section>
  );
};
